import React, { useEffect, useState } from "react";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";
import { useTranslation } from "react-i18next";
import { CurrentLoginPage, Severity } from "node-library/dist/constants";
import { useNavigate } from "react-router-dom";
import { Card } from "@mui/material";
import Config from "../../globals/Config";

import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import {
	CustomCheckbox,
	CustomTextInput,
} from "../../common-components/inputs";
import { CustomIconButton, BlueButton } from "../../common-components/button";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { validateEmail } from "../../helpers/inputHelper";
import { YellowWarnIcon } from "../../common-components/toast/warnIcons";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { ButtonTextLoadingAnimation } from "../../common-components/animation/ButtonLoadingAnimation";

interface Props {
	setCurrentPage: React.Dispatch<React.SetStateAction<CurrentLoginPage>>;
}

export default function ForgetPasswordPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const navigate = useNavigate();
	const [email, setEmail] = useState<string>("");

	const [loading, setLoading] = useState<boolean>(false);
	const [isDisabled, setIsDisabled] = useState(false);

	const [emailError, setEmailError] = useState<string>("");

	const [timer, setTimer] = useState<number>(0); // To track the remaining time for the countdown

	const [forgetPasswordStep, setForgetPasswordStep] = useState<number>(0);

	const [maskedPhoneNumber, setMaskedPhoneNumber] = useState<string>();
	const [phoneNumber, setPhoneNumber] = useState<string>("");
	const [phoneNumberError, setPhoneNumberError] = useState<string>("");

	function goBack() {
		props.setCurrentPage(CurrentLoginPage.LOGIN);
	}

	useEffect(() => {
		let interval: NodeJS.Timeout;

		if (isDisabled && timer > 0) {
			interval = setInterval(() => {
				setTimer((prevTimer) => prevTimer - 1);
			}, 1000);
		} else if (timer <= 0) {
			setIsDisabled(false); // Re-enable the button once the timer is finished
		}

		return () => clearInterval(interval); // Clean up the interval on component unmount or when timer reaches 0
	}, [isDisabled, timer]);

	const handleStepOneSubmit = async () => {
		if (!email) {
			setEmailError(translation("MissingInformation"));
			return;
		}
		if (!validateEmail(email)) {
			setEmailError(translation("InvalidEmail"));
			return;
		}

		setEmailError(""); // Clear error

		//recaptcha then register
		window.grecaptcha.enterprise.ready(() => {
			window.grecaptcha.enterprise
				.execute(Config.reCaptchaKey, { action: "forgetPasswordGetuser" })
				.then(async (token: string) => {
					try {
						setLoading(true);
						const data = await sendPOSTRequest(
							"/logins/forget-password-get-user",
							{
								userName: email,
								recaptchaValue: token,
							}
						);
						const maskedPhoneNumber: string = data.maskedPhoneNumber;
						setMaskedPhoneNumber(maskedPhoneNumber);
						setForgetPasswordStep(1);
					} catch (err: any) {
						if (err.response?.data === "User Does Not Exist!") {
							setEmailError(translation("InvalidAccount"));
						} else if (err.response?.status === 429) {
							const retryAfter =
								err.response.headers["retry-after"] ||
								err.response.headers["Retry-After"];

							const waitTime = parseInt(retryAfter, 10) || 60; // Fallback to 60 seconds if no retry-after header is present

							setTimer(waitTime); // Set the countdown timer
							setIsDisabled(true); // Disable the button
							openSnackbar(
								translation("TooManyRequest"),
								Severity.WARN,
								translation("TooManyRequestDescription"),
								<YellowWarnIcon />
							);
						} else {
							openSnackbar(
								translation("SystemError"),
								Severity.ERROR,
								translation("SystemErrorMsg"),
								<RedErrorIcon />
							);
						}
					} finally {
						setLoading(false);
					}
				});
		});
	};

	const handleSubmit = async () => {
		if (!phoneNumber || phoneNumber.length < 7) {
			setPhoneNumberError(translation("InvalidPhoneNumber"));
			return;
		}

		setPhoneNumberError(""); // Clear error

		//recaptcha then register
		window.grecaptcha.enterprise.ready(() => {
			window.grecaptcha.enterprise
				.execute(Config.reCaptchaKey, { action: "forgetPassword" })
				.then(async (token: string) => {
					try {
						setLoading(true);
						const data = await sendPOSTRequest("/logins/forget-password", {
							userName: email,
							phoneNumber: phoneNumber,
							recaptchaValue: token,
						});
						props.setCurrentPage(CurrentLoginPage.FORGET_PASSWORD_SUCCESS);
					} catch (err: any) {
						if (err.response?.data === "Incorrect Phone Number!") {
							setPhoneNumberError(translation("IncorrectPhoneNumber"));
						} else if (err.response?.status === 429) {
							const retryAfter =
								err.response.headers["retry-after"] ||
								err.response.headers["Retry-After"];

							const waitTime = parseInt(retryAfter, 10) || 60; // Fallback to 60 seconds if no retry-after header is present

							setTimer(waitTime); // Set the countdown timer
							setIsDisabled(true); // Disable the button
							openSnackbar(
								translation("TooManyRequest"),
								Severity.WARN,
								translation("TooManyRequestDescription"),
								<YellowWarnIcon />
							);
						} else {
							openSnackbar(
								translation("SystemError"),
								Severity.ERROR,
								translation("SystemErrorMsg"),
								<RedErrorIcon />
							);
						}
					} finally {
						setLoading(false);
					}
				});
		});
	};

	const stepOneContent: React.ReactNode = (
		<>
			<div className="typography-body-02" style={{ marginBottom: "10px" }}>
				<span>{`${translation("ForgetPasswordDescription")} `}</span>
			</div>
			<div>
				<CustomTextInput
					id={`forget-pwd-email-input`}
					value={email}
					setValue={(value) => {
						setEmail(value);
					}}
					label={translation("Email")}
					required
					errorMsg={emailError}
					setErrorMsg={setEmailError}
				/>
			</div>
			<div
				className="recaptcha-terms typography-caption"
				style={{ marginBottom: "20px" }}
			>
				This site is protected by reCAPTCHA and the Google{" "}
				<a href="https://policies.google.com/privacy"> Privacy Policy</a> and{" "}
				<a href="https://policies.google.com/terms"> Terms of Service</a> apply.
			</div>
			<BlueButton
				id="login-button"
				children={
					loading ? (
						<ButtonTextLoadingAnimation
							text={translation("Loading")}
						></ButtonTextLoadingAnimation>
					) : isDisabled ? (
						translation("PleaseWaitSeconds", { time: timer })
					) : (
						translation("Next")
					)
				}
				size={"flex"}
				disabled={loading || isDisabled}
				onClick={() => {
					handleStepOneSubmit();
				}}
			></BlueButton>
		</>
	);

	const stepTwoContent: React.ReactNode = (
		<>
			<div className="typography-body-02" style={{ marginBottom: "10px" }}>
				<span>{`${translation("ForgetPasswordStepTwoDescription")} `}</span>
			</div>
			<div
				className="typography-body-02"
				style={{ marginBottom: "10px", color: PrimaryColor.PRIMARY_600 }}
			>
				<span>{`${translation("PhoneNumberOnAccount", {
					maskedPhoneNumber: maskedPhoneNumber,
				})} `}</span>
			</div>
			<div>
				<CustomTextInput
					id={`forget-pwd-phone-number-input`}
					value={phoneNumber}
					setValue={(value) => {
						setPhoneNumber(value);
					}}
					label={translation("PhoneNumber")}
					required
					errorMsg={phoneNumberError}
					setErrorMsg={setPhoneNumberError}
				/>
			</div>
			<div
				className="recaptcha-terms typography-caption"
				style={{ marginBottom: "20px" }}
			>
				This site is protected by reCAPTCHA and the Google{" "}
				<a href="https://policies.google.com/privacy"> Privacy Policy</a> and{" "}
				<a href="https://policies.google.com/terms"> Terms of Service</a> apply.
			</div>
			<BlueButton
				id="login-button"
				children={
					loading ? (
						<ButtonTextLoadingAnimation
							text={translation("Loading")}
						></ButtonTextLoadingAnimation>
					) : isDisabled ? (
						translation("PleaseWaitSeconds", { time: timer })
					) : (
						translation("SendInstructions")
					)
				}
				size={"flex"}
				disabled={loading || isDisabled}
				onClick={() => {
					handleSubmit();
				}}
			></BlueButton>
		</>
	);

	let forgetPasswordContent: React.ReactNode = stepOneContent;

	switch (forgetPasswordStep) {
		case 0:
			forgetPasswordContent = stepOneContent;
			break;
		case 1:
			forgetPasswordContent = stepTwoContent;
			break;
	}

	return (
		<div className="login-container">
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "10px",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
					<CustomIconButton
						id="register-back-button"
						onClick={() => goBack()}
						icon={<KeyboardArrowLeftIcon />}
					></CustomIconButton>
					<div className="typography-heading-01 login-heading-01 ">
						<div>{translation("ForgetPassword")}</div>
					</div>
				</div>

				<div
					className="forget-password-icon"
					style={{ margin: "10px 0px 10px 5px" }}
				></div>

				{forgetPasswordContent}
			</div>
		</div>
	);
}
